"use client";

import { useState, type ReactNode } from "react";
import { MapPin } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  StandaloneAddressAutocomplete,
  type ParsedAddress,
} from "@/components/inputs/address-autocomplete";
import { ManualAddressEntry } from "@/components/address-book/components/manual-address-entry";
import { ADDRESS_CONSTRAINTS, formatAddressLocality } from "@shared/formats/address";

export interface VerifiedAddressValue {
  line1?: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
}

interface VerifiedAddressFieldProps {
  id: string;
  label: string;
  value: VerifiedAddressValue | null;
  onPick: (address: ParsedAddress) => void;
  onLine2Change: (line2: string | undefined) => void;
  onQueryChange?: (query: string) => void;
  placeholder: string;
  regionCodes: string[];
  includeCanada?: boolean;
  required?: boolean;
  error?: string;
  hint?: ReactNode;
  status?: ReactNode;
  line2Placeholder?: string;
}

export function VerifiedAddressField(props: VerifiedAddressFieldProps) {
  const [isChanging, setIsChanging] = useState(false);
  const pick = (address: ParsedAddress) => {
    props.onPick(address);
    setIsChanging(false);
  };
  if (props.value && !isChanging) {
    return (
      <PickedAddress
        {...props}
        value={props.value}
        onChangeAddress={() => setIsChanging(true)}
      />
    );
  }
  return (
    <AddressSearch
      {...props}
      onPick={pick}
      onCancel={props.value ? () => setIsChanging(false) : undefined}
    />
  );
}

type SearchProps = VerifiedAddressFieldProps & { onCancel?: () => void };

function AddressSearch(props: SearchProps) {
  const { id, includeCanada = false, onPick, onCancel } = props;
  return (
    <div className="grid gap-3">
      <AddressSearchField {...props} />
      <ManualAddressEntry
        disabled={false}
        includeCanada={includeCanada}
        idPrefix={`${id}-manual`}
        submitLabel="Use this address"
        onSubmit={(_, parsed) => onPick(parsed)}
      />
      {onCancel ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          className="justify-self-start"
        >
          Keep current address
        </Button>
      ) : null}
    </div>
  );
}

function AddressSearchField({
  id,
  label,
  onPick,
  onQueryChange,
  placeholder,
  regionCodes,
  required,
  error,
  hint,
  status,
  onCancel,
}: SearchProps) {
  const [query, setQuery] = useState("");
  return (
    <Field controlId={id} className="gap-2" data-invalid={Boolean(error)}>
      <FieldLabel className="text-sm text-foreground" required={required}>
        {label}
      </FieldLabel>
      <StandaloneAddressAutocomplete
        id={id}
        value={query}
        onChange={(display, parsed) => {
          if (parsed) {
            setQuery("");
            onPick(parsed);
            return;
          }
          setQuery(display);
          onQueryChange?.(display);
        }}
        placeholder={placeholder}
        inputClassName="text-base"
        regionCodes={regionCodes}
        required={required}
        autoFocus={Boolean(onCancel)}
        unavailableHint="Address search is unavailable right now. Enter the address manually below."
        aria-invalid={Boolean(error)}
      />
      {status}
      <FieldError className="text-sm">{error}</FieldError>
      {hint && !error ? (
        <FieldDescription className="text-sm">{hint}</FieldDescription>
      ) : null}
    </Field>
  );
}

function PickedAddress({
  id,
  label,
  value,
  error,
  onLine2Change,
  onChangeAddress,
  line2Placeholder = "Apt, suite, unit, or dock",
}: VerifiedAddressFieldProps & {
  value: VerifiedAddressValue;
  onChangeAddress: () => void;
}) {
  const line2Id = `${id}-line2`;
  return (
    <div className="grid gap-4">
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <AddressSummaryCard
          id={id}
          label={label}
          value={value}
          onChangeAddress={onChangeAddress}
        />
        {error ? <p className="text-sm text-destructive-strong">{error}</p> : null}
      </div>
      {value.line1 ? (
        <Field controlId={line2Id} className="gap-2">
          <FieldLabel className="text-sm text-foreground" optional>
            Address line 2
          </FieldLabel>
          <Input
            id={line2Id}
            value={value.line2 ?? ""}
            onChange={(event) => onLine2Change(event.target.value || undefined)}
            placeholder={line2Placeholder}
            autoComplete="address-line2"
            maxLength={ADDRESS_CONSTRAINTS.line2.maxLength}
            className="text-base"
          />
        </Field>
      ) : null}
    </div>
  );
}

function AddressSummaryCard({
  id,
  label,
  value,
  onChangeAddress,
}: {
  id: string;
  label: string;
  value: VerifiedAddressValue;
  onChangeAddress: () => void;
}) {
  const locality = formatAddressLocality(value);
  const summary = [value.line1, locality].filter(Boolean).join(", ");
  return (
    <Card padding="compact" className="flex-row items-center gap-3">
      <MapPin className="size-5 shrink-0 text-action" aria-hidden />
      <div className="min-w-0 flex-1 text-sm">
        <p className="break-words font-medium text-foreground">
          {value.line1 || locality}
        </p>
        {value.line1 ? <p className="text-muted-foreground">{locality}</p> : null}
      </div>
      <Button
        id={id}
        type="button"
        variant="link"
        size="bare"
        onClick={onChangeAddress}
        className="underline"
        aria-label={`Change ${label.toLowerCase()}, ${summary}`}
      >
        Change
      </Button>
    </Card>
  );
}
